import React, { useState, useContext, useRef, useEffect } from 'react';
import { each } from 'lodash';
import { MarkupContext, FocusBaseContext } from '../../../contexts';
import { defineHeatmapRadius } from '../../../helpers';

const ImageMarkups = () => {
  const {
    changeConceptStage, setChangeConceptStage,
    conceptData, conceptImage, setSelectedSquare, tab,
    threshold, resolution, opacity
  } = useContext(MarkupContext);
  const {
    markupTool, currentSummary, onReloadMarkupsRequest, onMarkupSettingsChange
  }  = useContext(FocusBaseContext);
  const [ loadedImage, setLoadedImage ] = useState(() => ({
    width: 0, height: 0, heatmapScale: 1, heatmapRenderWidth: 0
  }));
  const colors = currentSummary.colors;
  const greenColor = colors.positive || '#4ec09a';
  const redColor = colors.negative || '#ff7970';
  const orangeColor = colors.confusing || '#d0b050';
  const borderColor = '#000000';
  const frameColor = '#29A5FF';
  const defaultBorderOpacity = 0.9;
  const highlightOpacity = 0.6;
  const topStyles = useRef({ width: null, height: null });
  const heatmapInstance = useRef(null);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasInnerRef = useRef(null);

  const getThreshold = (threshold) => ((threshold || 50) / 100);
  const getMinThreshold = (threshold) => ((1 - getThreshold(threshold)));
  const getMaxThreshold = (maxValue, threshold) => (maxValue * (2 - getThreshold(threshold)));

  const markupsList = () => (markupTool.getMarkupComments());

  const heatmapCanvasElement = () => (
    heatmapInstance.current ? heatmapInstance.current._renderer.canvas : null
  );

  const changeCanvasOpacity = () => {
    const markupCanvas = canvasRef.current;
    if (markupCanvas) {
      markupCanvas.style.setProperty('opacity', '0');
    }
    const heatmapCanvas = heatmapCanvasElement();
    if (heatmapCanvas) {
      heatmapCanvas.style.setProperty('opacity', '0');
    }
    if (!tab) {
      if (markupCanvas) {
        markupCanvas.style.setProperty('opacity', '1');
      }
    } else if (heatmapCanvas) {
      heatmapCanvas.style.setProperty('opacity', '1');
    }
  };

  const getScaledArea = (baseWidth, area, imageWidth) => {
    const scale = baseWidth / parseFloat(imageWidth);
    const scaledArea = {
      left: parseFloat(area.left) * scale,
      top: parseFloat(area.top) * scale,
      width: parseFloat(area.width) * scale,
      height: parseFloat(area.height) * scale
    };
    scaledArea.right = scaledArea.left + scaledArea.width;
    scaledArea.bottom = scaledArea.top + scaledArea.height;
    return scaledArea;
  };

  const drawMarkup = (ctx, comment, alpha, frameRect, anyHighlighted, newLoadedImage) => {
    each(comment.filterAreas, (area) => {
      if (area.isHighlighted) {
        ctx.globalAlpha = highlightOpacity;
      } else if (anyHighlighted) {
        ctx.globalAlpha = 0.00000000000001;
      } else {
        ctx.globalAlpha = alpha;
      }

      if (comment.reaction === 'positive') {
        ctx.fillStyle = greenColor;
      }
      if (comment.reaction === 'negative') {
        ctx.fillStyle = redColor;
      }
      if (comment.reaction === 'confusing') {
        ctx.fillStyle = orangeColor;
      }

      const scaledArea = getScaledArea(
        (newLoadedImage || loadedImage).width, area, comment.image_width
      );

      ctx.fillRect(scaledArea.left, scaledArea.top, scaledArea.width, scaledArea.height);

      if (area.isSelected) {
        ctx.globalAlpha = defaultBorderOpacity;
        ctx.strokeStyle = borderColor;
        ctx.strokeRect(scaledArea.left, scaledArea.top, scaledArea.width, scaledArea.height);

        const newFrame = {};

        if (frameRect.left == null || frameRect.left > scaledArea.left) {
          newFrame.left = scaledArea.left;
        } else {
          newFrame.left = frameRect.left;
        }

        if (frameRect.top == null || frameRect.top > scaledArea.top) {
          newFrame.top = scaledArea.top;
        } else {
          newFrame.top = frameRect.top;
        }

        if (
          frameRect.width == null ||
          (frameRect.left + frameRect.width) < (scaledArea.left + scaledArea.width)
        ) {
          if (frameRect.width == null) {
            newFrame.width = scaledArea.width;
          } else {
            newFrame.width = (scaledArea.left + scaledArea.width) - newFrame.left;
          }
        } else {
          newFrame.width = (frameRect.left + frameRect.width) - newFrame.left;
        }

        if (
          frameRect.height == null ||
          (frameRect.top + frameRect.height) < (scaledArea.top + scaledArea.height)
        ) {
          if (frameRect.height == null) {
            newFrame.height = scaledArea.height;
          } else {
            newFrame.height = (scaledArea.top + scaledArea.height) - newFrame.top;
          }
        } else {
          newFrame.height = (frameRect.top + frameRect.height) - newFrame.top;
        }

        frameRect.left = newFrame.left;
        frameRect.top = newFrame.top;
        frameRect.width = newFrame.width;
        frameRect.height = newFrame.height;
      }
      ctx.globalAlpha = 1;
    });
  };

  const renderMarkups = (newLoadedImage) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    context.clearRect(0, 0, canvas.width, canvas.height);

    const frameRect = { left: null, top: null, width: null, height: null };

    context.setLineDash([]);
    each(markupsList(), (el) => {
      drawMarkup(context, el, opacity, frameRect, markupTool.anyHighlighted, newLoadedImage);
    });

    if (frameRect.left) {
      context.strokeStyle = frameColor;
      context.setLineDash([ 5, 2 ]);
      context.strokeRect(
        frameRect.left - 2, frameRect.top - 2, frameRect.width + 4, frameRect.height + 4
      );
      setSelectedSquare({
        x: frameRect.left + frameRect.width + 4,
        y: frameRect.top - 3
      });
    }
  };

  const destroyCanvas = () => {
    if (!heatmapInstance.current) {
      return;
    }
    const canvas = heatmapCanvasElement();
    canvas.remove();
    heatmapInstance.current = null;
  };

  const renderHeatmap = (newLoadedImage) => {
    const data = newLoadedImage || loadedImage;
    const heatmapAreaSizeX = data.heatmapRenderWidth / resolution;
    const heatmapAreaSizeY = data.height / resolution;

    const nuConfig = {
      container: canvasInnerRef.current,
      radius: defineHeatmapRadius(heatmapAreaSizeX, heatmapAreaSizeY, resolution),
      maxOpacity: 0.5,
      minOpacity: 0,
      blur: 0.75,
      scale: data.heatmapScale
    };
    destroyCanvas();
    heatmapInstance.current = h337.create(nuConfig);
    changeCanvasOpacity();

    const cells = [];
    let pX, pY;
    let maxCount = 0;

    const temp = [];
    for (temp; temp.push([]) < resolution;) {}

    each(markupsList(), (comment) => {
      each(comment.filterAreas, (area) => {
        const scaledArea = getScaledArea(data.heatmapRenderWidth, area, comment.image_width);

        if (!!scaledArea && scaledArea.width) {
          const x1 = Math.floor(scaledArea.left / heatmapAreaSizeX);
          const y1 = Math.floor(scaledArea.top / heatmapAreaSizeY);
          const x2 = Math.floor((scaledArea.left + scaledArea.width) / heatmapAreaSizeX);
          const y2 = Math.floor((scaledArea.top + scaledArea.height) / heatmapAreaSizeY);
          for (let i = x1; i <= x2; i++) {
            if (!temp[i]) {
              temp[i] = [];
            }

            for (let j = y1; j <= y2; j++) {
              if (!temp[i][j]) {
                temp[i][j] = { c: 0, id: null };
              }
              if (temp[i][j].id !== comment.id) {
                temp[i][j].c = temp[i][j].c + (comment.counter || 1);
                temp[i][j].id = comment.id;
              }
            }
          }
        }
      });
    });

    for (let i = 0; i < resolution; i++) {
      for (let j = 0; j < resolution; j++) {
        if (temp[i][j]) {
          if (temp[i][j].c > maxCount) {
            maxCount = temp[i][j].c;
          }
          pX = i * heatmapAreaSizeX;
          pY = j * heatmapAreaSizeY;
          cells.push({
            value: temp[i][j].c,
            x: pX + heatmapAreaSizeX / 2,
            y: pY + heatmapAreaSizeY / 2
          });
        }
      }
    }

    if (heatmapInstance.current) {
      heatmapInstance.current.setData(
        {
          max: getMaxThreshold(maxCount, threshold),
          min: getMinThreshold(threshold),
          data: cells
        }
      );
    }
  };

  const handleImageLoad = (event) => {
    setTimeout(() => {
      const imageBox = imageRef.current.getBoundingClientRect();
      const width = imageBox.width;
      const height = imageBox.height;
      canvasRef.current.width = width;
      canvasRef.current.height = height;
      markupTool.setWidth(width);
      const newLoadedImage = { ...loadedImage, ...{ width, height, heatmapRenderWidth: width } };
      setLoadedImage(newLoadedImage);
      setChangeConceptStage(null);
      onMarkupSettingsChange();
    }, 100);
  };

  useEffect(() => {
    renderMarkups();
    renderHeatmap();
  }, [ onReloadMarkupsRequest ]);

  useEffect(() => {
    changeCanvasOpacity();
  }, [ tab ]);

  return (
    <div className="survey-reactions-interface_image-canvas-wrapper" style={ topStyles.current }>
      <div
        ref={ canvasInnerRef }
        className="survey-reactions-interface_image-canvas-inner"
        style={ { opacity: (changeConceptStage ? 0 : 1) } }
      >
        <img
          ref={ imageRef }
          className="survey-reactions-interface_image"
          src={ conceptImage }
          onLoad={ handleImageLoad }
          alt={ conceptData.name }
        />
        <canvas
          ref={ canvasRef }
          className="survey-reactions-interface_canvas"
        />
      </div>
    </div>
  );
};

export default ImageMarkups;
